import { useEffect, useRef, useState } from "react";

import { useSearchParams } from "react-router-dom";

import { Spin } from "antd";

import {
  renderLabel,
  genRenderItem,
  exportTemplate
} from "../../libs/echar";

import Chart, { EChartsOption } from "../../components/Chart";

import {
  createK,
  calcYAxis,
  precision,
  findIntersectionPoint,
  createAuxiliaryData,
  createDiameterData,
  createSpeedData,
  createFrequencyData,
  parseSpuPointsDataset
} from "../../utils";

import useSpuPoints, {
  SpuPointsDataset,
  PerformanceFormField,
  SpuPointsInfo
} from "../../stores/spuPoints";

import { DataPoint } from "regression";
import { LineSeriesOption } from "echarts/charts";
import { useTranslation } from "react-i18next";
import { EChartsType } from "echarts/core";
import { DisplayUnits } from "../SpuChart/displayUnits/definitions";
import {
  convertFlowFromCanonical,
  convertFlowToCanonical,
  convertHeadToCanonical,
  convertNpshrFromCanonical,
  formatAxisLabel,
  getDisplayUnitLabel
} from "../SpuChart/displayUnits/converters";
import {
  buildPerformanceBaseDisplayData,
  buildPerformanceDisplayModel,
  buildPerformanceFormDisplayValues
} from "../SpuChart/displayUnits/adapter";
import {
  buildIntegerAxisLabelFormatter,
  FLOW_AXIS_SCALE_DIVISOR,
  formatFlowAxisName,
  formatDisplayMetricNumber,
  shouldUseFlowAxisHundreds,
} from "../SpuChart/displayUnits/displayFormatters";
import { buildLeadingDashedSeries } from "../SpuChart/leadingSegments";
import { buildPerformanceChartDebugPayload } from "../SpuChart/chartDebug";
import {
  solveViscousAnchoredCurve
} from "../SpuChart/viscosity/corrections";
import {
  buildBasePowerDisplayCurve,
  buildExtendedPowerDisplayCurve
} from "../SpuChart/viscosity/powerCurve";
import {
  boardCurveLabelStyle,
  boardMarkerStyles,
  createBoardGrid,
  createBoardTooltip,
  createBoardToolbox,
  createBoardValueAxis,
  ndBoardTheme
} from "../SpuChart/boardTheme";
import PerformanceInfoOverlay, {
  PerformanceInfoOverlayItem
} from "../SpuChart/components/PerformanceInfoOverlay";
import { downloadPerformanceChartImage } from "../SpuChart/components/exportPerformanceChartImage";

import "../Spu/Performance/PerformanceChart.css";

// 性能曲线图 [有时候图的 tooltip 并不是不对, 只是QH 和 QH2的 点位并不能一一对上]

interface PerformanceChartPrintProps {
  renderType?: any;
  name: string;
  info: SpuPointsInfo;
  dataset: SpuPointsDataset;
  performance: PerformanceFormField;
  displayUnits?: DisplayUnits;
  setParamsWithDisplayUnits?: (values: Record<string, string | number | undefined>) => void;
}

declare global {
  interface Window {
    __xzpPerformanceCurveDebug?: any;
  }
}

type ChartSetOptionMode = "merge" | "replace" | "clear-then-set";

const parseDebugSeriesFilter = (raw: string | null): string[] =>
  (raw || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const PerformanceChartPrint = ({
  renderType = 'default', // 类型 table: 表格里面 popover: 弹出层里面 default: 默认
  name, // 名字
  info, // 频率那些 基础数据
  dataset, // 点 基础数据
  performance, // 输入数据, q h sg
  displayUnits,
  setParamsWithDisplayUnits
}: PerformanceChartPrintProps) => {
  const { t } = useTranslation();
  const [drawLoading, setDrawLoading] = useState(false);
  const [debugTrace, setDebugTrace] = useState<any>(null);
  const [overlayTopItems, setOverlayTopItems] = useState<PerformanceInfoOverlayItem[]>([]);
  const [overlayBottomItems, setOverlayBottomItems] = useState<PerformanceInfoOverlayItem[]>([]);
  const chartInstanceRef = useRef<EChartsType | null>(null);
  const overlayTopItemsRef = useRef<PerformanceInfoOverlayItem[]>([]);
  const overlayBottomItemsRef = useRef<PerformanceInfoOverlayItem[]>([]);
  const finalDisplayUnits: DisplayUnits = displayUnits || {
    flow: "m3/h",
    head: "m",
    power: "kW",
    npshr: "m",
    diameter: "mm",
    viscosity: "cSt",
    efficiency: "%"
  };

  const {
    performanceForm,
    setPerformanceValue,
    setPerformanceResult
  } = useSpuPoints();

  const [searchParams, setSearchParams] = useSearchParams();
  const chartDebugConfig: {
    disableLeadingSegments: boolean;
    disableBaseSmooth: boolean;
    showRenderTrace: boolean;
    rawBaseSeries: boolean;
    disableTooltip: boolean;
    setOptionMode: ChartSetOptionMode;
    seriesFilter: string[];
  } = {
    disableLeadingSegments:
      searchParams.get("debugDisableLeadingSegments") === "1" ||
      searchParams.get("debugDisableLeadingSegments") === "true",
    disableBaseSmooth:
      searchParams.get("debugDisableBaseSmooth") === "1" ||
      searchParams.get("debugDisableBaseSmooth") === "true",
    showRenderTrace:
      searchParams.get("debugShowRenderTrace") === "1" ||
      searchParams.get("debugShowRenderTrace") === "true",
    rawBaseSeries:
      searchParams.get("debugRawBaseSeries") === "1" ||
      searchParams.get("debugRawBaseSeries") === "true",
    disableTooltip:
      searchParams.get("debugDisableTooltip") === "1" ||
      searchParams.get("debugDisableTooltip") === "true",
    setOptionMode:
      searchParams.get("debugSetOptionMode") === "replace" ||
      searchParams.get("debugSetOptionMode") === "clear-then-set"
        ? (searchParams.get("debugSetOptionMode") as "replace" | "clear-then-set")
        : "merge",
    seriesFilter: parseDebugSeriesFilter(searchParams.get("debugSeries")),
  };

  let initialOption: EChartsOption = {};

  if (renderType === 'default') {
    initialOption = {
      backgroundColor: ndBoardTheme.background,
      tooltip: chartDebugConfig.disableTooltip ? { show: false, trigger: "none" } : createBoardTooltip(),
      toolbox: createBoardToolbox(name, () => {
        if (!chartInstanceRef.current) {
          return;
        }

        downloadPerformanceChartImage(
          chartInstanceRef.current,
          name,
          overlayTopItemsRef.current,
          overlayBottomItemsRef.current
        );
      }),
      grid: [
        createBoardGrid({ left: 44, right: 44, top: 38, bottom: "40%" }),
        createBoardGrid({ left: 44, right: 44, top: "70%", bottom: 34 })
      ],
      xAxis: [
        createBoardValueAxis({
          name: formatAxisLabel("flow", finalDisplayUnits.flow),
          splitNumber: 10,
          nameGap: 18,
          axisLabel: {
            formatter: buildIntegerAxisLabelFormatter("flow"),
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("flow")(value, 0),
            },
          },
        }),
        createBoardValueAxis({
          name: formatAxisLabel("flow", finalDisplayUnits.flow),
          gridIndex: 1,
          splitNumber: 10,
          nameGap: 18,
          axisLabel: {
            formatter: buildIntegerAxisLabelFormatter("flow"),
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("flow")(value, 0),
            },
          },
        })
      ]
    };
  }

  if (renderType === 'popover') {
    initialOption = {
      backgroundColor: ndBoardTheme.background,
      tooltip: chartDebugConfig.disableTooltip ? { show: false, trigger: "none" } : createBoardTooltip(),
      grid: [
        createBoardGrid({ left: 44, right: 44, top: 38, bottom: "40%" }),
        createBoardGrid({ left: 44, right: 44, top: "70%", bottom: 34 })
      ],
      xAxis: [
        createBoardValueAxis({
          name: formatAxisLabel("flow", finalDisplayUnits.flow),
          nameGap: 18,
          axisLabel: {
            formatter: buildIntegerAxisLabelFormatter("flow"),
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("flow")(value, 0),
            },
          },
        }),
        createBoardValueAxis({
          name: formatAxisLabel("flow", finalDisplayUnits.flow),
          gridIndex: 1,
          nameGap: 18,
          axisLabel: {
            formatter: buildIntegerAxisLabelFormatter("flow"),
          },
          axisPointer: {
            label: {
              formatter: ({ value }: any) => buildIntegerAxisLabelFormatter("flow")(value, 0),
            },
          },
        })
      ]
    };
  }

  if (renderType === 'table') {
    initialOption = {
      xAxis: [
        {
          name: formatAxisLabel("flow", finalDisplayUnits.flow),
          type: "value",
          silent: true,
          axisLabel: {
            show: false
          }
        }
      ]
    };
  }

  const [option, setOption] = useState(initialOption);

  useEffect(() => {
    const {
      quantity,
      head,
      sg,
      viscosity,
      type // 选择是公式1 还是 公式2
    } = performance;

    // 对原始数据进行线性回归
    const {
      QH,
      QP,
      QEFF,
      QNPSH
    } = parseSpuPointsDataset(dataset);

    const displayDataSource = buildPerformanceBaseDisplayData({
      QH,
      QP,
      QEFF,
      QNPSH
    } as any, finalDisplayUnits);
    const displayBasePowerData = buildBasePowerDisplayCurve(displayDataSource.QP);
    const QHDummyPoints = displayDataSource.QH;
    const QEFFDummyPoints = displayDataSource.QEFF;
    const flowAxisMax = Math.max(
      0,
      ...displayDataSource.QH.map((point) => point[0] || 0),
      ...displayDataSource.QP.map((point) => point[0] || 0),
      ...displayDataSource.QEFF.map((point) => point[0] || 0),
      ...displayDataSource.QNPSH.map((point) => point[0] || 0)
    );
    const useScaledFlowAxis = shouldUseFlowAxisHundreds(flowAxisMax);
    const flowAxisLabelFormatter = buildIntegerAxisLabelFormatter("flow", {
      scaleDivisor: useScaledFlowAxis ? FLOW_AXIS_SCALE_DIVISOR : undefined,
    });
    const flowAxisName = formatFlowAxisName(
      formatAxisLabel("flow", finalDisplayUnits.flow),
      flowAxisMax
    );
    const flowAxisNameTextStyle = useScaledFlowAxis
      ? {
          lineHeight: 14,
          padding: [6, 0, 0, 0],
        }
      : undefined;

    // 找到共有区间
    let [hMax, hInterval] = calcYAxis(
      QHDummyPoints.map((p) => p[1]),
      40,
      1
    );

    let [pMax, pInterval] = calcYAxis(
      displayBasePowerData.map((p) => p[1]),
      20,
      1
    );

    let QEFFIndex = 0;

    for (let i in QEFFDummyPoints) {
      const v = QEFFDummyPoints[i];

      const y = v[1];

      if (y > 0) {
        QEFFIndex = Number(i);

        break;
      }
    }

    // const QHDash = QHDummyPoints.filter(v => v[0] < 3);
    // const QHLine = QHDummyPoints.filter(v => v[0] >= 3);

    // 创建 PerformanceChart 视图
    let initialSeries: LineSeriesOption[] = [];

    if (renderType === 'default') {
      setDrawLoading(true);
    }

    if (renderType === "default" || renderType === "popover") {
      setOverlayTopItems([]);
      setOverlayBottomItems([]);
    }

    if (renderType === 'default' || renderType === 'popover') {
      const rawBaseSeries: LineSeriesOption[] = [
        {
          name: "QH1",
          type: "line",
          // Keep board-style curve labels on the shared helper so empty label points
          // do not render background artifacts as a fake pale curve.
          label: renderLabel("Q-H", [20, -8], undefined, boardCurveLabelStyle),
          smooth: !chartDebugConfig.disableBaseSmooth, // 是否平滑
          symbolSize: 0, // 标记的大小
          lineStyle: {
            width: 1,
          },
          color: ndBoardTheme.referenceCurve,
          data: QHDummyPoints,
          animation: false, // 关闭动画
        },
        {
          name: "QEFF",
          type: "line",
          // Do not inline a plain boxed label here. This series depends on renderLabel
          // to isolate business label styling to the actual visible text point only.
          label: renderLabel("Q-EFF", [18, -8], QEFFIndex, boardCurveLabelStyle),
          smooth: !chartDebugConfig.disableBaseSmooth,
          symbolSize: 0,
          lineStyle: {
            width: 1,
          },
          color: ndBoardTheme.referenceCurve,
          data: displayDataSource.QEFF,
          xAxisIndex: 0,
          yAxisIndex: 1,
          animation: false
        },
        {
          name: "QP1",
          type: "line",
          label: renderLabel("Q-P", [18, -8], undefined, boardCurveLabelStyle),
          smooth: false,
          symbolSize: 0,
          lineStyle: {
            width: 1
          },
          color: ndBoardTheme.referenceCurve,
          data: displayBasePowerData,
          xAxisIndex: 1,
          yAxisIndex: 2,
          animation: false
        },
        {
          name: "QNPSH",
          type: "line",
          label: renderLabel("Q-NPSHr", [20, -8], undefined, boardCurveLabelStyle),
          smooth: !chartDebugConfig.disableBaseSmooth,
          emphasis: {
            disabled: true,
          },
          symbolSize: 0,
          lineStyle: {
            width: 1
          },
          data: displayDataSource.QNPSH,
          color: ndBoardTheme.referenceCurve,
          xAxisIndex: 1,
          yAxisIndex: 3,
          animation: false
        }
      ];

      initialSeries = chartDebugConfig.rawBaseSeries
        ? rawBaseSeries
        : [
            ...buildLeadingDashedSeries(rawBaseSeries[0], QHDummyPoints, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            ...buildLeadingDashedSeries(rawBaseSeries[1], displayDataSource.QEFF, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            ...buildLeadingDashedSeries(rawBaseSeries[2], displayBasePowerData, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            rawBaseSeries[3],
          ];
    }

    if (renderType === 'table') {
      const rawTableBaseSeries: LineSeriesOption[] = [
        {
          name: "QH1",
          type: "line",
          smooth: !chartDebugConfig.disableBaseSmooth, // 是否平滑
          symbolSize: 0, // 标记的大小
          lineStyle: {
            width: 1
          },
          data: QHDummyPoints,
          color: "#aaa",
          animation: false // 关闭动画
        },
        {
          name: "QEFF",
          type: "line",
          smooth: !chartDebugConfig.disableBaseSmooth,
          symbolSize: 0,
          lineStyle: {
            width: 1,
          },
          color: ndBoardTheme.referenceCurve,
          data: displayDataSource.QEFF,
          xAxisIndex: 0,
          yAxisIndex: 1,
          animation: false
        }
      ];
      initialSeries = chartDebugConfig.rawBaseSeries
        ? rawTableBaseSeries
        : [
            ...buildLeadingDashedSeries(rawTableBaseSeries[0], QHDummyPoints, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            ...buildLeadingDashedSeries(rawTableBaseSeries[1], displayDataSource.QEFF, {
              disabled: chartDebugConfig.disableLeadingSegments,
            })
          ];
    }

    let newOption: EChartsOption = {};

    if (renderType === 'popover') {
      const [hMaxP, hIntervalP] = calcYAxis(
        QHDummyPoints.map((p) => p[1]),
        10,
        1
      );

      const [pMaxP, pIntervalP] = calcYAxis(
        displayBasePowerData.map((p) => p[1]),
        5,
        1
      );

      hMax = hMaxP;
      hInterval = hIntervalP;

      pMax = pMaxP;
      pInterval = pIntervalP;
    }

    if (renderType === 'default' || renderType === 'popover') {
      newOption = {
        ...initialOption,
        xAxis: [
          createBoardValueAxis({
            name: flowAxisName,
            splitNumber: 10,
            nameGap: useScaledFlowAxis ? 30 : 18,
            nameTextStyle: flowAxisNameTextStyle,
            axisLabel: {
              formatter: flowAxisLabelFormatter,
            },
            axisPointer: {
              label: {
                formatter: ({ value }: any) => flowAxisLabelFormatter(value, 0),
              },
            },
          }),
          createBoardValueAxis({
            name: flowAxisName,
            gridIndex: 1,
            splitNumber: 10,
            nameGap: useScaledFlowAxis ? 30 : 18,
            nameTextStyle: flowAxisNameTextStyle,
            axisLabel: {
              formatter: flowAxisLabelFormatter,
            },
            axisPointer: {
              label: {
                formatter: ({ value }: any) => flowAxisLabelFormatter(value, 0),
              },
            },
          }),
        ],
        yAxis: [
          // H
          createBoardValueAxis({
            name: formatAxisLabel("head", finalDisplayUnits.head),
            min: 0,
            max: hMax,
            interval: hInterval,
            axisLabel: {
              showMinLabel: true,
              showMaxLabel: true,
              hideOverlap: true,
              formatter: buildIntegerAxisLabelFormatter("head", {
                shouldDisplay: (_value, index) => index % 5 === 0,
              }),
            },
            axisPointer: {
              label: {
                formatter: ({ value }: any) =>
                  buildIntegerAxisLabelFormatter("head")(value, 0),
              },
            },
          }),
          createBoardValueAxis({
            name: "EFF(%)",
            min: 0,
            max: 200,
            splitNumber: 10,
            axisPointer: {
              label: {
                show: false,
              },
            },
            axisLabel: {
              showMinLabel: true,
              showMaxLabel: true,
              hideOverlap: true,
              formatter: buildIntegerAxisLabelFormatter("efficiency", {
                shouldDisplay: (value) => value <= 100,
              }),
            }
          }),
          createBoardValueAxis({
            name: formatAxisLabel("power", finalDisplayUnits.power),
            min: 0,
            max: pMax,
            interval: pInterval,
            gridIndex: 1,
            axisLabel: {
              showMinLabel: true,
              showMaxLabel: true,
              hideOverlap: true,
              formatter: buildIntegerAxisLabelFormatter("power", {
                shouldDisplay: (_value, index) => index % 5 === 0,
              }),
            },
            axisPointer: {
              label: {
                formatter: ({ value }: any) =>
                  buildIntegerAxisLabelFormatter("power")(value, 0),
              },
            }
          }),
          createBoardValueAxis({
            name: formatAxisLabel("npshr", finalDisplayUnits.npshr),
            gridIndex: 1,
            min: 0,
            max: convertNpshrFromCanonical(15, finalDisplayUnits.npshr) as number,
            splitNumber: 5,
            axisPointer: {
              label: {
                show: false,
              },
            },
            axisLabel: {
              showMinLabel: true,
              showMaxLabel: true,
              hideOverlap: true
            }
          })
        ],
        series: initialSeries
      };
    }

    if (renderType === 'table') {
      newOption = {
        ...initialOption,
        xAxis: [
          {
            name: flowAxisName,
            type: "value",
            silent: true,
            axisLabel: {
              show: false
            }
          }
        ],
        yAxis: [
          // H
          {
            name: formatAxisLabel("head", finalDisplayUnits.head),
            type: "value",
            min: 0,
            max: hMax,
            interval: hInterval,
            silent: true,
            axisLabel: {
              show: false
            }
          },
          // EFF
          createBoardValueAxis({
            name: "EFF(%)",
            type: "value",
            min: 0,
            max: 200,
            splitNumber: 10,
            silent: true,
            axisLabel: {
              show: false
            }
          })
        ],
        series: initialSeries
      };
    }

    // 如果有表单数据，则绘制 QH2/QP2/QEFF2/QNPSH2 和对应文字
    if (quantity !== undefined && head !== undefined) {
      const {
        range
      } = info;

      // 获取区间中点
      const center = (range.start + range.end) / 2;

      // 获取辅助线与QH1的交点
      const intersectionPoint = findIntersectionPoint(
        quantity,
        head,
        QH.equation, // QH 方程式系数的数组
        type,
        center
      );

      if (intersectionPoint) {
        // 生成辅助线数据
        const auxData = createAuxiliaryData(
          quantity,
          head,
          0,
          intersectionPoint[0],
          type
        );

        // 根据交点 生成 K
        const k: number = createK(quantity, intersectionPoint[0], type);

        // 根据K 生成 QH2 [平移]
        const solvedCurve = solveViscousAnchoredCurve({
          quantity,
          head,
          sg,
          viscosityCanonical: viscosity,
          speed: info.speed,
          type,
          qhPoints: QH.dummyPoints,
          qeffPoints: QEFF.dummyPoints,
          qnpshPoints: QNPSH ? QNPSH.dummyPoints : [],
          qhRawPoints: QH.points as DataPoint[],
          qeffRawPoints: QEFF.points as DataPoint[],
          qhEquation: QH.equation,
          center
        });
        if (!solvedCurve) {
          return;
        }
        const correctedQH2Data = solvedCurve.viscousCurves.qH2v;
        const correctedQEFF2Data = solvedCurve.viscousCurves.qEFF2v;
        const correctedQNPSH2Data = solvedCurve.viscousCurves.qNPSH2v;
        // 计算叶轮尺寸
        const {
          diameter,
          diameterUnit
        } = createDiameterData(
          k,
          info.diameter,
          type
        );

        // 计算转速
        const speed = createSpeedData(k, info.speed, type);

        // 计算频率 [公式2 不计算, 直接返回]
        const frequency = createFrequencyData(k, info.frequency, type);

        const correctedDutyPoint = solvedCurve.correctedDutyPointResult;

        const finalQEFF2Data = correctedQEFF2Data.filter((v: any) => {
          const qEff = v[1];

          return qEff <= 100;
        }); // 过滤掉 效率 > 100的错误数据

        const finalQP2Data = buildExtendedPowerDisplayCurve(
          displayDataSource.QP,
          solvedCurve.viscousCurves.qP2vTrusted,
          {
            anchorPoint: [quantity, correctedDutyPoint.power]
          }
        );
        const displayModel = buildPerformanceDisplayModel({
          displayUnits: finalDisplayUnits,
          quantity,
          head,
          power: correctedDutyPoint.power,
          npsh: correctedDutyPoint.npsh ?? null,
          diameter,
          diameterUnit,
          auxData,
          qH2Data: correctedQH2Data,
          qEFF2Data: finalQEFF2Data,
          qNPSH2Data: correctedQNPSH2Data,
          qP2Data: finalQP2Data
        });

        let isPre = false;

        const auth = window.localStorage.getItem("aikonLoggedUser");
        let authData: any = {};

        if (auth) {
          authData = JSON.parse(auth);

          const {
            erpID
          } = authData || {};

          if (erpID === 13 || erpID === 14 || erpID === '13' || erpID === '14') {
            isPre = true;
          }
        }

        if (renderType === 'default' || renderType === 'popover') {
          // 更新表单
          setPerformanceResult({
            diameter,
            diameterUnit,
            speed,
            frequency,
            power: correctedDutyPoint.power,
            npsh: correctedDutyPoint.npsh,
            eff: correctedDutyPoint.eff,
          });

          const defaultSeries: any[] = [
            {
              name: "aux",
              type: "line",
              smooth: true,
              emphasis: {
                disabled: true,
              },
              symbolSize: 0,
              lineStyle: {
                width: 1.2
              },
              color: ndBoardTheme.auxiliaryCurve,
              data: displayModel.auxData,
              animation: false
            },
            ...buildLeadingDashedSeries({
              name: "QH2",
              type: "line",
              smooth: true,
              symbolSize: 0,
              lineStyle: {
                width: 1.2
              },
              color: ndBoardTheme.activeCurve,
              label: renderLabel(
                type === 0
                  ? `${precision(speed, 0)} RPM`
                  : `${precision(diameter, 0)} ${diameterUnit}`
              ),
              animation: false
            }, displayModel.qH2Data, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            ...buildLeadingDashedSeries({
              name: "QEFF2",
              type: "line",
              smooth: true,
              symbolSize: 0,
              lineStyle: {
                width: 1.2,
              },
              xAxisIndex: 0,
              yAxisIndex: 1,
              color: ndBoardTheme.supportCurve,
              animation: false
            }, displayModel.qEFF2Data, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            ...buildLeadingDashedSeries({
              name: "QP2",
              type: "line",
              smooth: false,
              symbolSize: 0,
              lineStyle: {
                width: 1.2
              },
              xAxisIndex: 1,
              yAxisIndex: 2,
              color: ndBoardTheme.supportCurve,
              animation: false
            }, displayModel.qP2Data, {
              disabled: chartDebugConfig.disableLeadingSegments,
            }),
            {
              name: "QNPSH2",
              type: "line",
              smooth: true,
              emphasis: {
                disabled: true,
              },
              symbolSize: 0,
              lineStyle: {
                width: 1.2
              },
              xAxisIndex: 1,
              yAxisIndex: 3,
              color: ndBoardTheme.supportCurve,
              data: displayModel.qNPSH2Data,
              animation: false
            },
            {
              name: "intersection point",
              type: "custom",
              renderItem: genRenderItem("circle1", displayModel.quantity, displayModel.head, undefined, { markerStyles: boardMarkerStyles }),
              data: [
                {
                  name: "intersection point",
                  xAxis: displayModel.quantity,
                  yAxis: displayModel.head
                }
              ],
              z: 100,
            },
            {
              name: "intersection point",
              type: "custom",
              renderItem: genRenderItem("circle2", displayModel.quantity, displayModel.head, undefined, { markerStyles: boardMarkerStyles }),
              data: [
                {
                  name: "intersection point",
                  xAxis: displayModel.quantity,
                  yAxis: displayModel.head
                }
              ],
              z: 101,
            },
            {
              name: "intersection point",
              type: "custom",
              renderItem: genRenderItem("circle3", displayModel.quantity, displayModel.head, undefined, { markerStyles: boardMarkerStyles }),
              data: [
                {
                  name: "intersection point",
                  xAxis: displayModel.quantity,
                  yAxis: displayModel.head
                }
              ],
              z: 102,
            },
          ];

          setOverlayTopItems([
            {
              label: isPre ? t("performanceChart.Q") : "Q",
              value: formatDisplayMetricNumber("flow", displayModel.quantity),
              unit: getDisplayUnitLabel("flow", finalDisplayUnits.flow),
            },
            {
              label: isPre ? t("performanceChart.H") : "H",
              value: formatDisplayMetricNumber("head", displayModel.head),
              unit: getDisplayUnitLabel("head", finalDisplayUnits.head),
            },
            {
              label: isPre ? t("performanceChart.diameter") : t("chart.diameter"),
              value: precision(displayModel.diameter ?? 0, 0),
              unit: displayModel.diameterDisplayUnit,
            },
            {
              label: isPre ? t("performanceChart.speed") : t("chart.speed"),
              value: precision(speed, 0),
              unit: "rpm",
            },
            {
              label: isPre ? t("performanceChart.efficiency") : t("chart.efficiency"),
              value: formatDisplayMetricNumber("efficiency", correctedDutyPoint.eff),
              unit: "%",
            },
            {
              label: isPre ? t("performanceChart.sg") : t("chart.sg"),
              value: precision(sg || 1, 2),
            },
          ]);

          setOverlayBottomItems([
            {
              label: "P",
              value: formatDisplayMetricNumber("power", displayModel.power),
              unit: getDisplayUnitLabel("power", finalDisplayUnits.power),
            },
            {
              label: "NPSHr",
              value:
                displayModel.npsh !== null && displayModel.npsh !== undefined
                  ? formatDisplayMetricNumber("npshr", displayModel.npsh)
                  : displayModel.npsh,
              unit: getDisplayUnitLabel("npshr", finalDisplayUnits.npshr),
            },
          ]);

          const finalDutyQ = convertFlowFromCanonical(quantity, finalDisplayUnits.flow) as number;
          const effQSeries: any[] = [
            {
              name: "intersection point",
              type: "custom",
              xAxisIndex: 0,
              yAxisIndex: 1,
              renderItem: genRenderItem("circle3", finalDutyQ, correctedDutyPoint.eff, undefined, { markerStyles: boardMarkerStyles }),
              data: [
                {
                  name: "intersection point",
                  xAxis: finalDutyQ,
                  yAxis: correctedDutyPoint.eff
                }
              ],
              z: 102,
            }
          ];

          const npsh2QSeries: any[] = correctedDutyPoint.npsh !== null && correctedDutyPoint.npsh !== undefined
            ? [
                {
                  name: "intersection point",
                  type: "custom",
                  xAxisIndex: 1,
                  yAxisIndex: 3,
                  renderItem: genRenderItem("circle3", finalDutyQ, displayModel.npsh as number, undefined, { markerStyles: boardMarkerStyles }),
                  data: [
                    {
                      name: "intersection point",
                      xAxis: finalDutyQ,
                      yAxis: displayModel.npsh
                    }
                  ],
                  z: 102,
                }
              ]
            : [];

          const qp2QSeries: any[] = [
            {
              name: "intersection point",
              type: "custom",
              xAxisIndex: 1,
              yAxisIndex: 2,
              renderItem: genRenderItem("circle3", finalDutyQ, displayModel.power, undefined, { markerStyles: boardMarkerStyles }),
              data: [
                {
                  name: "intersection point",
                  xAxis: finalDutyQ,
                  yAxis: displayModel.power
                }
              ],
              z: 102,
            }
          ];

          // 更新图表
          newOption = {
            ...newOption,
            series: [
              ...initialSeries,
              ...defaultSeries,
              ...effQSeries,
              ...npsh2QSeries,
              ...qp2QSeries
            ]
          };
        }

        if (renderType === 'table') {
          newOption = {
            ...newOption,
            series: [
              ...initialSeries,
              {
                name: "aux",
                type: "line",
                smooth: true,
                emphasis: {
                  disabled: true,
                },
                symbolSize: 0,
                lineStyle: {
                  width: 1.2
                },
                color: "black",
                data: displayModel.auxData,
                animation: false
              },
              {
                name: "QH2",
                type: "line",
                smooth: true,
                emphasis: {
                  disabled: true,
                },
                symbolSize: 0,
                color: "black",
                lineStyle: {
                  width: 1.2,
                },
                data: displayModel.qH2Data,
                animation: false
              },
              {
                name: "QEFF2",
                type: "line",
                smooth: true,
                emphasis: {
                  disabled: true,
                },
                symbolSize: 0,
                lineStyle: {
                  width: 1.2
                },
                xAxisIndex: 0,
                yAxisIndex: 1,
                color: "black",
                data: displayModel.qEFF2Data,
                animation: false
              },
            ]
          };
        }
      }
    }

    if (chartDebugConfig.seriesFilter.length > 0 && Array.isArray(newOption.series)) {
      newOption = {
        ...newOption,
        series: newOption.series.filter((series: any) =>
          chartDebugConfig.seriesFilter.includes(series?.name)
        ),
      };
    }

    setOption(newOption);

    if (renderType === 'default') {
      setDrawLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(performance),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(dataset),
    JSON.stringify(finalDisplayUnits),
    chartDebugConfig.disableLeadingSegments,
    chartDebugConfig.disableBaseSmooth,
    JSON.stringify(chartDebugConfig.seriesFilter)
  ]
  );

  useEffect(() => {
    overlayTopItemsRef.current = overlayTopItems;
    overlayBottomItemsRef.current = overlayBottomItems;
  }, [overlayTopItems, overlayBottomItems]);

  const handleChartChange = (instance: EChartsType) => {
    chartInstanceRef.current = instance;
    try {
      const debugPayload = buildPerformanceChartDebugPayload(instance, {
        instance,
        renderType,
        chartDebugConfig,
      });

      window.__xzpPerformanceCurveDebug = debugPayload;
      setDebugTrace(debugPayload);
      console.log("__xzpPerformanceCurveDebug", debugPayload);
    } catch (error) {
      console.warn("__xzpPerformanceCurveDebug_error", error);
    }

    instance.getDataURL(exportTemplate as any);
    instance.getDataURL();
  };

  const handleChartClick = (params: any, chart: any) => {
    const pixel = [params.offsetX, params.offsetY];

    if (chart && chart.containPixel({seriesIndex: 0}, pixel)) {
      const xAxisPointer = chart.getOption().xAxis[0].axisPointer;
      const yAxisPointer = chart.getOption().yAxis[0].axisPointer;

      const {
        value: xValue
      } = xAxisPointer;

      const {
        value: yValue
      } = yAxisPointer;

      const finalXValue: any = parseFloat((convertFlowToCanonical(xValue, finalDisplayUnits.flow) as number).toFixed(2))
      const finalYValue: any = parseFloat((convertHeadToCanonical(yValue, finalDisplayUnits.head) as number).toFixed(2))

      const {
        type,
        sg,
        qeff,
        viscosity,
      } = performance;
      const formDisplayValues = buildPerformanceFormDisplayValues({
        quantity: finalXValue,
        head: finalYValue
      }, finalDisplayUnits);

      performanceForm.setFieldsValue({
        quantity: formDisplayValues.quantity,
        head: formDisplayValues.head
      });

      if (setParamsWithDisplayUnits) {
        setParamsWithDisplayUnits({
          q: finalXValue,
          h: finalYValue,
          sg,
          viscosity,
        });
      } else {
        const nextParams = new URLSearchParams();
        nextParams.set("q", `${finalXValue}`);
        nextParams.set("h", `${finalYValue}`);

        if (sg !== undefined) {
          nextParams.set("sg", `${sg}`);
        }

        if (viscosity !== undefined) {
          nextParams.set("viscosity", `${viscosity}`);
        }

        setSearchParams(nextParams);
      }

      setPerformanceValue({
        type,
        sg,
        quantity: finalXValue,
        head: finalYValue,
        qeff,
        viscosity
      });
    }
  }

  if (renderType === 'table') {
    return (
      <Chart
        option={option}
        notMerge
        setOptionMode={chartDebugConfig.setOptionMode}
        style={{ width: 80, height: 80 }}
        className='performance-chart-table'
        onChange={handleChartChange}
        renderer="svg"
      />
    );
  }

  if (renderType === 'popover') {
    return (
      <div className="performance-chart-main" style={{ position: "relative", width: 500, height: 700, margin: "auto" }}>
        <Chart
          option={option}
          notMerge
          setOptionMode={chartDebugConfig.setOptionMode}
          style={{ width: 500, height: 700, margin: "auto" }}
          className='performance-chart'
          onChange={handleChartChange}
          renderer="svg"
        />
        <PerformanceInfoOverlay
          topItems={overlayTopItems}
          bottomItems={overlayBottomItems}
        />
      </div>
    )
  }

  return (
    <div className="performance-chart-main">
      {
        drawLoading && (
          <div className="pcm-loading">
            <Spin
              spinning={drawLoading}
              className='pcm-l-spin'
            />
          </div>
        )
      }
      <Chart
        option={option}
        notMerge
        setOptionMode={chartDebugConfig.setOptionMode}
        style={{ width: 500, height: 700, margin: "auto" }}
        className='performance-chart'
        onChange={handleChartChange} // 图表变换时的回调函数
        renderer="svg"
        handleChartClick={handleChartClick}
      />
      <PerformanceInfoOverlay
        topItems={overlayTopItems}
        bottomItems={overlayBottomItems}
      />
      {chartDebugConfig.showRenderTrace && debugTrace && (
        <pre
          style={{
            width: 500,
            margin: "12px auto 0",
            maxHeight: 220,
            overflow: "auto",
            background: "#0f1720",
            color: "#d7e3ef",
            padding: 12,
            fontSize: 11,
            lineHeight: 1.4,
            whiteSpace: "pre-wrap",
          }}
        >
          {JSON.stringify(
            {
              explicitSeriesCount: debugTrace.explicitSeriesCount,
              displayLineObjectCount: debugTrace.displayLineObjectCount,
              domCanvasSummary: debugTrace.domCanvasSummary,
              xAxisSummary: debugTrace.xAxisSummary,
              yAxisSummary: debugTrace.yAxisSummary,
              optionSeries: debugTrace.optionSeries?.map((series: any) => ({
                name: series.name,
                color: series.color,
                lineDash: series.lineDash,
                dataCount: series.dataCount,
              })),
              slantedLineSegmentsByStroke: debugTrace.slantedLineSegmentsByStroke,
            },
            null,
            2
          )}
        </pre>
      )}
    </div>
  );
};

export default PerformanceChartPrint;
